import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, PawPrint, Home as HomeIcon, HandHeart, Shield, ArrowRight, Sparkles, Waves, ShoppingBag } from "lucide-react";
import { api } from "@/lib/api";
import DogImage from "@/components/DogImage";
import { ZEFFY_MAIN, ZEFFY_SPIKE, HERO_VIDEO, HERO_VIDEO_FALLBACK, HERO_POSTER, SHOPIFY_URL } from "@/constants/config";
import ImpactCounter from "@/components/ImpactCounter";
import FeaturedSponsors from "@/components/FeaturedSponsors";
import PageMeta from "@/components/PageMeta";
import OrgJsonLd from "@/components/OrgJsonLd";

const HAWAIIAN_WORDS = [
  { word: "ʻOhana", meaning: "Family — no one gets left behind" },
  { word: "Mālama", meaning: "To care, to protect, to serve" },
  { word: "Aloha", meaning: "Love, compassion, presence of breath" },
  { word: "Kōkua", meaning: "To help, to lend a hand" },
  { word: "Mahalo", meaning: "Thank you, from a full heart" },
];

export default function Home() {
  const [dogs, setDogs] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dogs").then((r) => {
      const all = r.data || [];
      setFeatured(all.find((d) => d.featured) || null);
      // Only show truly available dogs (exclude adopted + featured)
      const availableOnly = all.filter((d) => d.status === "available" && !d.featured);
      setDogs(availableOnly.slice(0, 3));
    }).catch(() => {});
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <PageMeta
        title="Lavish 808 Bulliez Rescued Paws — Hawaiian Bulldog Rescue in Murrieta, CA"
        description="501(c)(3) Hawaiian-inspired bulldog rescue based in Murrieta, Riverside County. We rehabilitate and rehome bulldogs, French bulldogs and bully breeds across Southern California and Hawaiʻi. Every Paw Deserves a Second Chance at a Lavish Life."
      />
      <OrgJsonLd />
      <Hero />
      {featured && <FeaturedCampaign featured={featured} />}
      <MissionBento stats={stats} />
      <ImpactCounter />
      <FeaturedSponsors />
      <AlohaValues />
      <HawaiianWords />
      <MeetDogs dogs={dogs} />
      <ShopTheirStories />
      <CtaBanner />
    </div>
  );
}

function ShopTheirStories() {
  return (
    <section className="py-20" style={{ backgroundColor: "#F7ECD9" }} data-testid="shop-stories-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: "#E27E4E" }}>Shop with Aloha</div>
        <h2 className="font-serif text-4xl md:text-5xl mb-4" style={{ color: "#0E5C6E" }}>
          Wear their story.
        </h2>
        <p className="max-w-2xl mx-auto mb-10 leading-relaxed" style={{ color: "#2D2B2A" }}>
          Every shirt, tumbler, hat, and sticker helps fund vet care, food, transport, and rehabilitation.
          Every purchase helps another paw get a second chance at a Lavish life.
        </p>
        <a
          href={SHOPIFY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-donate inline-flex"
          data-testid="home-shop-button"
        >
          <ShoppingBag size={18} /> Visit the Shop
        </a>
        <div className="text-xs mt-6" style={{ color: "#6B655E" }}>
          Every purchase supports rescue, veterinary care, food, transportation, rehabilitation, and rehoming.
        </div>
      </div>
    </section>
  );
}

function Hero() {
  return (
    <section className="video-hero h-[92vh] min-h-[620px] flex items-end" data-testid="hero-section">
      <video autoPlay loop muted playsInline poster={HERO_POSTER}>
        <source src={HERO_VIDEO} type="video/mp4" />
        <source src={HERO_VIDEO_FALLBACK} type="video/mp4" />
      </video>
      <div className="overlay" />
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pb-20 md:pb-28 text-white">
        <div className="fade-up">
          <div className="text-xs uppercase tracking-[0.35em] text-white/90 mb-6 flex items-center gap-3">
            <span className="w-8 h-px bg-white/60" /> Mālama • To Care
          </div>
        </div>
        <h1 className="fade-up delay-100 font-serif text-5xl sm:text-7xl md:text-8xl leading-[0.95] tracking-tight max-w-4xl mb-6">
          Every paw deserves a <em className="text-[#F4C99F]">second chance</em>.
        </h1>
        <p className="fade-up delay-200 font-sans text-lg sm:text-xl text-white/90 max-w-2xl mb-10 leading-relaxed">
          From Hawaiʻi to the West Coast, Lavish 808 Bulliez Rescued Paws rescues,
          rehabilitates, and rehomes bulldogs, French bulldogs, bully breeds, and companion
          animals through compassion, veterinary care, and the spirit of Aloha.
        </p>
        <div className="fade-up delay-300 flex flex-wrap gap-4">
          <a href={ZEFFY_MAIN} target="_blank" rel="noopener noreferrer" className="btn-donate" data-testid="hero-donate-button">
            <Heart size={18} fill="white" /> Donate Now
          </a>
          <Link to="/adopt" className="btn-outline-white" data-testid="hero-adopt-button">
            <PawPrint size={18} /> Adopt a Dog
          </Link>
          <Link to="/get-involved" className="btn-outline-white" data-testid="hero-foster-button">
            <HomeIcon size={18} /> Become a Foster
          </Link>
        </div>
        <div className="mt-10 flex items-center gap-4 text-white/70 text-xs uppercase tracking-[0.25em]">
          <Waves size={16} /> Every Paw Deserves a Second Chance at a Lavish Life
        </div>
      </div>
    </section>
  );
}

function FeaturedCampaign({ featured }) {
  return (
    <section className="py-16 md:py-20" style={{ backgroundColor: "#F7ECD9" }} data-testid="featured-campaign">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-5 relative">
          <div className="rounded-3xl overflow-hidden aspect-[4/5]">
            <DogImage dog={featured} />
          </div>
          <div className="absolute -top-4 -right-4 badge badge-urgent shadow-md">
            <Sparkles size={12} /> Kōkua Needed
          </div>
        </div>
        <div className="md:col-span-7">
          <div className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: "#E27E4E" }}>
            Featured Campaign • Mālama {featured.name}
          </div>
          <h2 className="font-serif text-4xl md:text-5xl mb-6" style={{ color: "#0E5C6E" }}>
            Help {featured.name} see the world again.
          </h2>
          <p className="text-lg leading-relaxed mb-8" style={{ color: "#2D2B2A" }}>{featured.story}</p>
          <div className="flex flex-wrap gap-4">
            <a href={featured.zeffy_url || ZEFFY_SPIKE} target="_blank" rel="noopener noreferrer" className="btn-donate" data-testid="spike-donate-button">
              <Heart size={18} fill="white" /> Fund {featured.name}'s Surgery
            </a>
            <Link to={`/adopt`} className="btn-outline" data-testid="spike-learn-more">
              Meet all our ʻohana <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function MissionBento({ stats }) {
  return (
    <section className="py-20 md:py-32" data-testid="mission-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          <div className="md:col-span-7 p-8 md:p-14 rounded-3xl" style={{ backgroundColor: "#F7ECD9" }}>
            <div className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: "#E27E4E" }}>
              Mālama means "to care"
            </div>
            <h2 className="font-serif text-4xl md:text-5xl mb-6" style={{ color: "#0E5C6E" }}>
              Every rescue begins with compassion.
            </h2>
            <p className="text-lg leading-relaxed" style={{ color: "#2D2B2A" }}>
              At Lavish 808 Bulliez Rescued Paws, every rescue begins with
              compassion. We are committed to providing the best possible care for our animals —
              ensuring every one finds their forever ʻohana.
            </p>
          </div>
          <div className="md:col-span-5 rounded-3xl overflow-hidden aspect-square md:aspect-auto">
            <img src="https://images.unsplash.com/photo-1601245381370-4056f3f47526?w=800&q=80" alt="Bulldog rescued and rehomed by Lavish 808 Bulliez" loading="lazy" decoding="async" className="w-full h-full object-cover" />
          </div>

          <div className="md:col-span-4 rounded-3xl overflow-hidden aspect-square">
            <img src="https://images.unsplash.com/photo-1583337130417-3346a1be7dee?w=800&q=80" alt="Two bulldogs playing at the rescue" loading="lazy" decoding="async" className="w-full h-full object-cover" />
          </div>
          <div className="md:col-span-8 p-8 md:p-14 rounded-3xl relative overflow-hidden text-white" style={{ background: "linear-gradient(135deg, #0E5C6E 0%, #145466 40%, #E27E4E 100%)" }}>
            <div className="text-xs uppercase tracking-[0.3em] mb-4 text-white/70">Our Approach</div>
            <h3 className="font-serif text-3xl md:text-4xl text-white mb-6">
              Veterinary care, behavioral support, and loving foster homes — every step wrapped in Aloha.
            </h3>
            <p className="text-white/80 leading-relaxed max-w-2xl">
              We believe in building trust and fostering a sense of security for each rescued
              animal, preparing them for a joyful life with their forever ʻohana.
            </p>
            {stats && (
              <div className="mt-8 grid grid-cols-3 gap-4 text-white">
                <StatItem label="Available Now" value={stats.dogs_available} />
                <StatItem label="Adopted" value={stats.dogs_adopted} />
                <StatItem label="Since 2024" value="🌺" />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function AlohaValues() {
  return (
    <section className="py-20 md:py-32" style={{ backgroundColor: "#F7ECD9" }} data-testid="values-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: "#E27E4E" }}>Rescuing with Aloha</div>
          <h2 className="font-serif text-4xl md:text-5xl" style={{ color: "#0E5C6E" }}>
            The spirit of Aloha guides everything we do.
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-lg" style={{ color: "#2D2B2A" }}>
            It's about love, compassion, and respect for all living beings. We extend this spirit
            to every animal we rescue.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <ValueCard icon={<HandHeart />} title="Aloha" translate="Love" desc="Compassion, kindness, and respect — extended to every paw that crosses our path." />
          <ValueCard icon={<Shield />} title="Mālama" translate="Care" desc="Caring stewardship of every animal entrusted to us. No corners cut. No pup forgotten." />
          <ValueCard icon={<PawPrint />} title="Kuleana" translate="Responsibility" desc="Accountability, integrity, and the responsibility to see every rescue through." />
          <ValueCard icon={<HomeIcon />} title="ʻOhana" translate="Family" desc="Every rescued animal — every adopter, foster, and volunteer — is family. No one gets left behind." />
        </div>
      </div>
    </section>
  );
}

function HawaiianWords() {
  return (
    <section className="py-20 md:py-24" data-testid="hawaiian-words">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: "#E27E4E" }}>ʻŌlelo Hawaiʻi</div>
          <h2 className="font-serif text-3xl md:text-4xl" style={{ color: "#0E5C6E" }}>The words that guide us.</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6">
          {HAWAIIAN_WORDS.map((w) => (
            <div key={w.word} className="p-6 rounded-2xl bg-white border text-center" style={{ borderColor: "#EADCC5" }}>
              <div className="font-serif text-2xl md:text-3xl mb-2" style={{ color: "#E27E4E" }}>{w.word}</div>
              <div className="text-xs leading-relaxed" style={{ color: "#2D2B2A" }}>{w.meaning}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function MeetDogs({ dogs }) {
  return (
    <section className="py-20 md:py-28" style={{ backgroundColor: "#F7ECD9" }} data-testid="meet-dogs-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: "#E27E4E" }}>
              Looking for their ʻohana
            </div>
            <h2 className="font-serif text-4xl md:text-5xl" style={{ color: "#0E5C6E" }}>
              Meet our animals.
            </h2>
          </div>
          <Link to="/adopt" className="btn-outline" data-testid="home-view-all-dogs">
            View all <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {dogs.map((d) => (
            <Link key={d.id} to={`/adopt#${d.id}`} className="card-dog block" data-testid={`home-dog-card-${d.name.toLowerCase()}`}>
              <div className="aspect-[4/5] overflow-hidden">
                <DogImage dog={d} />
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-serif text-2xl" style={{ color: "#0E5C6E" }}>{d.name}</h3>
                  <span className={`badge badge-${d.featured ? "urgent" : d.status}`}>
                    {d.featured ? "Kōkua" : d.status}
                  </span>
                </div>
                <div className="text-sm" style={{ color: "#6B655E" }}>{d.breed} • {d.age} • {d.sex}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaBanner() {
  return (
    <section className="py-20 md:py-28" style={{ background: "linear-gradient(180deg, #0E5C6E 0%, #135665 50%, #C56439 100%)" }} data-testid="cta-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
        <div className="text-xs uppercase tracking-[0.3em] mb-4 text-white/70">Building forever ʻohana</div>
        <h2 className="font-serif text-4xl md:text-6xl mb-8">
          One rescue at a time. One hale at a time.
        </h2>
        <p className="text-white/80 text-lg max-w-2xl mx-auto mb-10">
          Whether you adopt, foster, volunteer, or kōkua with a donation — every act of Aloha changes a life.
        </p>
        <div className="flex flex-wrap gap-4 justify-center">
          <a href={ZEFFY_MAIN} target="_blank" rel="noopener noreferrer" className="btn-donate" data-testid="cta-donate-button">
            <Heart size={18} fill="white" /> Donate via Zeffy
          </a>
          <Link to="/get-involved" className="btn-outline-white" data-testid="cta-volunteer-button">
            <HandHeart size={18} /> Volunteer / Foster
          </Link>
        </div>
      </div>
    </section>
  );
}

function StatItem({ label, value }) {
  return (
    <div>
      <div className="font-serif text-4xl">{value}</div>
      <div className="text-xs uppercase tracking-[0.15em] text-white/60 mt-1">{label}</div>
    </div>
  );
}

function ValueCard({ icon, title, translate, desc }) {
  return (
    <div className="p-10 rounded-3xl bg-white border" style={{ borderColor: "#EADCC5" }}>
      <div className="w-14 h-14 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: "#0E5C6E", color: "white" }}>
        {icon}
      </div>
      <div className="text-xs uppercase tracking-[0.2em] mb-1" style={{ color: "#E27E4E" }}>{translate}</div>
      <h3 className="font-serif text-3xl mb-3" style={{ color: "#0E5C6E" }}>{title}</h3>
      <p style={{ color: "#2D2B2A" }} className="leading-relaxed">{desc}</p>
    </div>
  );
}
